/**
 * Content Security Policy (CSP) Service
 * Implements strict CSP headers and meta tags for XSS protection
 */

/**
 * CSP directive configuration
 */
interface CSPConfig {
  /** Allow inline scripts (use with nonce) */
  allowInlineScripts?: boolean;

  /** Allow inline styles (use with nonce) */
  allowInlineStyles?: boolean;

  /** Additional script sources */
  scriptSrc?: string[];

  /** Additional style sources */
  styleSrc?: string[];

  /** Additional image sources */
  imgSrc?: string[];

  /** Additional connect sources (API endpoints) */
  connectSrc?: string[];

  /** Additional font sources */
  fontSrc?: string[];

  /** Frame ancestors (who can embed this widget) */
  frameAncestors?: string[];

  /** Enable strict-dynamic for modern browsers */
  useStrictDynamic?: boolean;

  /** Nonce for inline scripts/styles */
  nonce?: string;

  /** Report URI for CSP violations */
  reportUri?: string;

  /** Report only mode (don't enforce) */
  reportOnly?: boolean;
}

/**
 * Default CSP configuration for the widget
 */
const DEFAULT_CSP_CONFIG: CSPConfig = {
  allowInlineScripts: false,
  allowInlineStyles: false,
  scriptSrc: [
    "'self'",
    'https://www.google.com/recaptcha/', // reCAPTCHA
    'https://www.gstatic.com/recaptcha/', // reCAPTCHA
  ],
  styleSrc: ["'self'"],
  imgSrc: ["'self'", 'data:', 'https:'],
  connectSrc: ["'self'"],
  fontSrc: ["'self'", 'data:'],
  frameAncestors: ["'self'"], // Production should be specific parent domains
  useStrictDynamic: true,
  reportOnly: false,
};

/**
 * CSP Service for managing Content Security Policy
 */
export class CSPService {
  private config: CSPConfig;
  private nonce: string | null = null;

  constructor(config: Partial<CSPConfig> = {}) {
    this.config = { ...DEFAULT_CSP_CONFIG, ...config };

    // Generate nonce if inline scripts/styles are allowed
    if (this.config.allowInlineScripts || this.config.allowInlineStyles) {
      this.nonce = this.generateNonce();
    }
  }

  /**
   * Generate cryptographically secure nonce
   */
  private generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array));
  }

  /**
   * Build CSP header value
   */
  public buildCSPHeader(): string {
    const directives: string[] = [];

    // Default-src: fallback for all resource types
    directives.push("default-src 'self'");

    // Script-src: JavaScript sources
    const scriptSrc = ["'self'", ...(this.config.scriptSrc || [])];
    if (this.config.allowInlineScripts && this.nonce) {
      scriptSrc.push(`'nonce-${this.nonce}'`);
    }
    if (this.config.useStrictDynamic) {
      scriptSrc.push("'strict-dynamic'");
    }
    directives.push(`script-src ${scriptSrc.join(' ')}`);

    // Style-src: CSS sources
    const styleSrc = ["'self'", ...(this.config.styleSrc || [])];
    if (this.config.allowInlineStyles && this.nonce) {
      styleSrc.push(`'nonce-${this.nonce}'`);
    }
    directives.push(`style-src ${styleSrc.join(' ')}`);

    // Img-src: Image sources
    if (this.config.imgSrc && this.config.imgSrc.length > 0) {
      directives.push(`img-src ${this.config.imgSrc.join(' ')}`);
    }

    // Connect-src: AJAX, WebSocket, EventSource
    if (this.config.connectSrc && this.config.connectSrc.length > 0) {
      directives.push(`connect-src ${this.config.connectSrc.join(' ')}`);
    }

    // Font-src: Font sources
    if (this.config.fontSrc && this.config.fontSrc.length > 0) {
      directives.push(`font-src ${this.config.fontSrc.join(' ')}`);
    }

    // Frame-ancestors: Who can embed this widget
    // NOTE: frame-ancestors cannot be set via meta tag - must be HTTP header
    // This is set by Cloudflare Worker, not via meta tag
    // if (this.config.frameAncestors && this.config.frameAncestors.length > 0) {
    //   directives.push(`frame-ancestors ${this.config.frameAncestors.join(' ')}`);
    // }

    // Object-src: Disallow plugins
    directives.push("object-src 'none'");

    // Base-uri: Restrict <base> tag
    directives.push("base-uri 'self'");

    // Form-action: Restrict form submissions
    directives.push("form-action 'self'");

    // Frame-src: Disallow nested frames
    directives.push("frame-src 'none'");

    // Upgrade-insecure-requests: Force HTTPS
    directives.push('upgrade-insecure-requests');

    // Block-all-mixed-content: No HTTP on HTTPS pages
    directives.push('block-all-mixed-content');

    // Report-uri: CSP violation reporting
    if (this.config.reportUri) {
      directives.push(`report-uri ${this.config.reportUri}`);
    }

    return directives.join('; ');
  }

  /**
   * Create CSP meta tag
   */
  public createMetaTag(): HTMLMetaElement {
    const meta = document.createElement('meta');
    meta.httpEquiv = this.config.reportOnly
      ? 'Content-Security-Policy-Report-Only'
      : 'Content-Security-Policy';
    meta.content = this.buildCSPHeader();
    return meta;
  }

  /**
   * Inject CSP meta tag into document
   */
  public inject(): void {
    const existingMeta = document.querySelector(
      'meta[http-equiv="Content-Security-Policy"], meta[http-equiv="Content-Security-Policy-Report-Only"]'
    );

    if (existingMeta) {
      existingMeta.remove();
    }

    const meta = this.createMetaTag();
    document.head.insertBefore(meta, document.head.firstChild);

    console.log('[CSP] Injected Content Security Policy:', this.buildCSPHeader());
  }

  /**
   * Get nonce for inline scripts/styles
   */
  public getNonce(): string | null {
    return this.nonce;
  }

  /**
   * Validate if a source is allowed by CSP
   */
  public isSourceAllowed(source: string, type: 'script' | 'style' | 'img' | 'connect'): boolean {
    const allowedSources = this.getAllowedSources(type);

    // Check if source matches any allowed pattern
    return allowedSources.some((allowed) => {
      if (allowed === "'self'") {
        try {
          const url = new URL(source, window.location.origin);
          return url.origin === window.location.origin;
        } catch {
          return false;
        }
      }

      if (allowed === '*' || allowed === 'https:') {
        return source.startsWith('https://');
      }

      return source.startsWith(allowed);
    });
  }

  /**
   * Get allowed sources for a resource type
   */
  private getAllowedSources(type: 'script' | 'style' | 'img' | 'connect'): string[] {
    switch (type) {
      case 'script':
        return ["'self'", ...(this.config.scriptSrc || [])];
      case 'style':
        return ["'self'", ...(this.config.styleSrc || [])];
      case 'img':
        return this.config.imgSrc || [];
      case 'connect':
        return this.config.connectSrc || [];
      default:
        return ["'self'"];
    }
  }

  /**
   * Log CSP violation (for development)
   */
  public static setupViolationReporting(callback?: (violation: SecurityPolicyViolationEvent) => void): void {
    document.addEventListener('securitypolicyviolation', (e) => {
      console.error('[CSP Violation]', {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        originalPolicy: e.originalPolicy,
        disposition: e.disposition,
      });

      if (callback) {
        callback(e);
      }
    });
  }
}

/**
 * Singleton CSP service instance
 */
let cspServiceInstance: CSPService | null = null;

/**
 * Initialize CSP service
 */
export function initCSPService(config?: Partial<CSPConfig>): CSPService {
  if (cspServiceInstance) {
    console.warn('[CSP] Service already initialized');
    return cspServiceInstance;
  }

  cspServiceInstance = new CSPService(config);
  cspServiceInstance.inject();

  // Setup violation reporting in development
  if (import.meta.env.MODE === 'development') {
    CSPService.setupViolationReporting();
  }

  return cspServiceInstance;
}

/**
 * Get CSP service instance
 */
export function getCSPService(): CSPService {
  if (!cspServiceInstance) {
    throw new Error('CSPService not initialized. Call initCSPService first.');
  }
  return cspServiceInstance;
}
